<template>
    <div class="uri-encoder">
        <!-- <div class="header">
            <div class="title-container">
                <h1 class="main-title">
                    <span class="title-icon">🔗</span>
                    <span class="title-text">URI 编码/解码工具</span>
                </h1>
                <p class="subtitle">理解和使用 encodeURIComponent 与 decodeURIComponent</p>
            </div>
        </div> -->

        <div class="content-wrapper">

            <!-- 实际演示区域 -->
            <div class="demo-section">
                <div class="section-card">
                    <div class="section-header">
                        <h2 class="section-title">
                            <span class="section-icon">🎮</span>
                            编码/解码区域
                        </h2>
                    </div>

                    <div class="demo-container">
                        <!-- 编码演示 -->
                        <div class="demo-group encode-demo">
                            <div class="demo-header">
                                <h3 class="demo-title">编码 (encodeURIComponent)</h3>
                                <button @click="insertEncodeExample" class="example-btn" title="插入示例">
                                    <span class="btn-icon">📋</span>
                                    示例
                                </button>
                            </div>

                            <div class="input-group">
                                <label class="input-label">输入要编码的文本：</label>
                                <textarea v-model="encodeInput" class="demo-textarea" placeholder="输入包含特殊字符的文本..."
                                    rows="3" @input="performEncode" spellcheck="false"></textarea>
                                <div class="input-meta">
                                    <span class="char-count">字符数: {{ encodeInput.length }}</span>
                                    <span class="char-types" v-if="encodeInput">
                                        {{ countSpecialChars(encodeInput) }} 个特殊字符
                                    </span>
                                </div>
                            </div>

                            <div class="result-group" v-if="encodedResult">
                                <label class="result-label">编码结果：</label>
                                <div class="result-display">
                                    <code class="result-code">{{ encodedResult }}</code>
                                    <button @click="copyResult('encode')" class="copy-btn" title="复制结果">
                                        <span class="copy-icon">📋</span>
                                    </button>
                                </div>
                                <div class="result-meta">
                                    <span class="result-length">编码后长度: {{ encodedResult.length }}</span>
                                    <span class="encoding-info">UTF-8 百分比编码</span>
                                </div>
                            </div>

                            <div class="char-analysis" v-if="encodeInput">
                                <h4 class="analysis-title">字符分析</h4>
                                <div class="char-grid">
                                    <div v-for="char in encodeInput" :key="char + Math.random()" class="char-item"
                                        :class="{
                                            'safe': isSafeChar(char),
                                            'encoded': !isSafeChar(char)
                                        }" :title="`${char} (${char.charCodeAt(0).toString(16)})`">
                                        <span class="char-display">{{ char === ' ' ? '␣' : char }}</span>
                                        <span class="char-code">{{ !isSafeChar(char) ? char.charCodeAt(0).toString(16) :
                                            '' }}</span>
                                    </div>
                                </div>
                                <div class="legend">
                                    <div class="legend-item safe">
                                        <span class="legend-color"></span>
                                        <span class="legend-text">安全字符（不编码）</span>
                                    </div>
                                    <div class="legend-item encoded">
                                        <span class="legend-color"></span>
                                        <span class="legend-text">编码字符（转为%XX）</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 解码演示 -->
                        <div class="demo-group decode-demo">
                            <div class="demo-header">
                                <h3 class="demo-title">解码 (decodeURIComponent)</h3>
                                <button @click="insertDecodeExample" class="example-btn" title="插入示例">
                                    <span class="btn-icon">📋</span>
                                    示例
                                </button>
                            </div>

                            <div class="input-group">
                                <label class="input-label">输入要解码的编码字符串：</label>
                                <textarea v-model="decodeInput" class="demo-textarea" placeholder="输入百分比编码的字符串..."
                                    rows="3" @input="performDecode" spellcheck="false"></textarea>
                                <div class="input-meta">
                                    <span class="char-count">字符数: {{ decodeInput.length }}</span>
                                    <span class="percent-sequences" v-if="decodeInput">
                                        {{ countPercentSequences(decodeInput) }} 个编码序列
                                    </span>
                                </div>
                            </div>

                            <div class="result-group" v-if="decodedResult !== null">
                                <label class="result-label">解码结果：</label>
                                <div class="result-display">
                                    <code class="result-code">{{ decodedResult || '(空字符串)' }}</code>
                                    <button @click="copyResult('decode')" class="copy-btn" title="复制结果">
                                        <span class="copy-icon">📋</span>
                                    </button>
                                </div>
                                <div class="result-meta">
                                    <span class="result-length">解码后长度: {{ decodedResult?.length || 0 }}</span>
                                    <span class="decode-status" :class="{ 'error': decodeError }">
                                        {{ decodeError ? '解码错误' : '解码成功' }}
                                    </span>
                                </div>
                                <div class="error-message" v-if="decodeError">
                                    <span class="error-icon">❌</span>
                                    {{ decodeError }}
                                </div>
                            </div>

                            <div class="encoding-breakdown" v-if="decodeInput && !decodeError">
                                <h4 class="analysis-title">编码序列解析</h4>
                                <div class="sequence-grid">
                                    <div v-for="(seq, index) in extractPercentSequences(decodeInput)" :key="index"
                                        class="sequence-item">
                                        <span class="sequence-code">{{ seq }}</span>
                                        <span class="sequence-arrow">→</span>
                                        <span class="sequence-char">{{ decodePercentSequence(seq) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 实际应用示例 -->
                    <div class="real-world-example">
                        <h3 class="example-title">🌐 实际应用示例</h3>
                        <div class="url-example">
                            <div class="url-original">
                                <span class="url-label">原始查询参数：</span>
                                <code class="url-code">name=张三&city=New York&search=hello world?&price=100$</code>
                            </div>
                            <div class="url-encoded">
                                <span class="url-label">编码后的URL：</span>
                                <code
                                    class="url-code">https://example.com/search?q=<span class="highlight">encodeURIComponent('hello world?')</span></code>
                            </div>
                            <div class="url-result">
                                <span class="url-label">实际URL：</span>
                                <code class="url-code">https://example.com/search?q=hello%20world%3F</code>
                            </div>
                        </div>

                        <div class="code-snippet">
                            <pre class="snippet-code"><code>// 构建包含特殊字符的URL
const query = 'hello world?';
const encodedQuery = encodeURIComponent(query);
// 结果: 'hello%20world%3F'

const url = `https://api.example.com/search?q=${encodedQuery}`;
// 完整URL: 'https://api.example.com/search?q=hello%20world%3F'

// 在服务器端解码
const receivedQuery = decodeURIComponent('hello%20world%3F');
// 结果: 'hello world?'</code></pre>
                        </div>
                    </div>
                </div>
            </div>
            <!-- 理论解释区域 -->
            <div class="theory-section">
                <div class="section-card">
                    <div class="section-header">
                        <h2 class="section-title">
                            <span class="section-icon">📚</span>
                            什么是 URI 编码？
                        </h2>
                    </div>

                    <div class="theory-content">
                        <div class="definition">
                            <p class="definition-text">
                                <strong>URI 编码</strong>（也称为百分比编码）是将 URI（统一资源标识符）中的特殊字符转换为安全格式的过程，以便在网络上传输。
                            </p>
                        </div>

                        <div class="comparison-grid">
                            <div class="function-card encode-card">
                                <div class="function-header">
                                    <div class="function-icon">🔒</div>
                                    <h3 class="function-title">encodeURIComponent()</h3>
                                </div>
                                <div class="function-content">
                                    <p class="function-desc">
                                        对 URI 组件进行编码，将所有字符（除了字母、数字和 <code>- _ . ! ~ * ' ( )</code>）转换为百分比编码。
                                    </p>
                                    <div class="use-case">
                                        <span class="use-case-label">主要用途：</span>
                                        <ul class="use-case-list">
                                            <li>编码查询参数</li>
                                            <li>编码 URL 片段</li>
                                            <li>编码表单数据</li>
                                        </ul>
                                    </div>
                                    <div class="example">
                                        <span class="example-label">示例：</span>
                                        <code class="example-code">encodeURIComponent('hello world?')</code>
                                        <div class="example-result">→ <code>hello%20world%3F</code></div>
                                    </div>
                                </div>
                            </div>

                            <div class="function-card decode-card">
                                <div class="function-header">
                                    <div class="function-icon">🔓</div>
                                    <h3 class="function-title">decodeURIComponent()</h3>
                                </div>
                                <div class="function-content">
                                    <p class="function-desc">
                                        解码由 <code>encodeURIComponent</code> 编码的 URI 组件，将百分比编码转换回原始字符。
                                    </p>
                                    <div class="use-case">
                                        <span class="use-case-label">主要用途：</span>
                                        <ul class="use-case-list">
                                            <li>解码接收到的查询参数</li>
                                            <li>解码 URL 片段</li>
                                            <li>处理编码的表单数据</li>
                                        </ul>
                                    </div>
                                    <div class="example">
                                        <span class="example-label">示例：</span>
                                        <code class="example-code">decodeURIComponent('hello%20world%3F')</code>
                                        <div class="example-result">→ <code>hello world?</code></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="important-note">
                            <div class="note-header">
                                <span class="note-icon">⚠️</span>
                                <h4 class="note-title">重要区别</h4>
                            </div>
                            <div class="note-content">
                                <p><code>encodeURIComponent()</code> 与 <code>encodeURI()</code> 不同：</p>
                                <ul class="difference-list">
                                    <li><strong>encodeURIComponent</strong>：编码整个组件，包括保留字符如 <code>?</code>,
                                        <code>&</code>, <code>=</code>, <code>/</code></li>
                                    <li><strong>encodeURI</strong>：编码整个 URI，但保留 URI 结构字符</li>
                                </ul>
                                <div class="comparison-example">
                                    <div class="comparison-item">
                                        <span class="comparison-label">encodeURIComponent('hello?world'):</span>
                                        <code class="comparison-code">hello%3Fworld</code>
                                    </div>
                                    <div class="comparison-item">
                                        <span class="comparison-label">encodeURI('hello?world'):</span>
                                        <code class="comparison-code">hello?world</code>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <!-- 总结区域 -->
            <div class="summary-section">
                <div class="section-card">
                    <div class="section-header">
                        <h2 class="section-title">
                            <span class="section-icon">✅</span>
                            使用总结
                        </h2>
                    </div>

                    <div class="summary-content">
                        <div class="summary-points">
                            <div class="point-card">
                                <div class="point-icon">🎯</div>
                                <h4 class="point-title">何时使用 encodeURIComponent</h4>
                                <ul class="point-list">
                                    <li>构建 URL 查询字符串时</li>
                                    <li>处理用户输入作为 URL 参数时</li>
                                    <li>编码表单数据发送到服务器时</li>
                                    <li>需要确保特殊字符不会破坏 URL 结构时</li>
                                </ul>
                            </div>

                            <div class="point-card">
                                <div class="point-icon">🎯</div>
                                <h4 class="point-title">何时使用 decodeURIComponent</h4>
                                <ul class="point-list">
                                    <li>从 URL 参数中获取数据时</li>
                                    <li>处理服务器返回的编码数据时</li>
                                    <li>解析包含特殊字符的查询字符串时</li>
                                    <li>需要将百分比编码转换回可读文本时</li>
                                </ul>
                            </div>

                            <div class="point-card">
                                <div class="point-icon">⚠️</div>
                                <h4 class="point-title">注意事项</h4>
                                <ul class="point-list">
                                    <li>不要对完整 URL 使用 encodeURIComponent</li>
                                    <li>避免多次编码/解码（会导致错误）</li>
                                    <li>注意中文字符会转换为多字节编码</li>
                                    <li>确保编码和解码使用相同的字符集</li>
                                </ul>
                            </div>
                        </div>

                        <div class="cheatsheet">
                            <h4 class="cheatsheet-title">📋 快速参考表</h4>
                            <table class="cheatsheet-table">
                                <thead>
                                    <tr>
                                        <th>字符</th>
                                        <th>encodeURIComponent</th>
                                        <th>说明</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>空格</code></td>
                                        <td><code>%20</code></td>
                                        <td>URL 中空格必须编码</td>
                                    </tr>
                                    <tr>
                                        <td><code>?</code></td>
                                        <td><code>%3F</code></td>
                                        <td>查询参数分隔符</td>
                                    </tr>
                                    <tr>
                                        <td><code>&</code></td>
                                        <td><code>%26</code></td>
                                        <td>参数分隔符</td>
                                    </tr>
                                    <tr>
                                        <td><code>=</code></td>
                                        <td><code>%3D</code></td>
                                        <td>键值对分隔符</td>
                                    </tr>
                                    <tr>
                                        <td><code>中文</code></td>
                                        <td><code>%E4%B8%AD%E6%96%87</code></td>
                                        <td>多字节字符编码</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 复制通知 -->
        <div v-if="showCopyNotification" class="copy-notification">
            <span class="copy-icon">✓</span> 已复制到剪贴板！
        </div>
    </div>
</template>

<script>
export default {
    name: 'URIEncoderDecoder',
    data() {
        return {
            encodeInput: '',
            decodeInput: '',
            encodedResult: '',
            decodedResult: null,
            decodeError: '',
            showCopyNotification: false
        };
    },
    methods: {
        // 执行编码
        performEncode() {
            if (!this.encodeInput) {
                this.encodedResult = '';
                return;
            }

            try {
                this.encodedResult = encodeURIComponent(this.encodeInput);
            } catch (error) {
                console.error('编码错误:', error);
                this.encodedResult = '编码错误';
            }
        },

        // 执行解码
        performDecode() {
            if (!this.decodeInput) {
                this.decodedResult = null;
                this.decodeError = '';
                return;
            }

            try {
                this.decodedResult = decodeURIComponent(this.decodeInput);
                this.decodeError = '';
            } catch (error) {
                console.error('解码错误:', error);
                this.decodedResult = null;
                this.decodeError = error.message;
            }
        },

        // 插入编码示例
        insertEncodeExample() {
            this.encodeInput = 'Hello 世界! How are you? & fine=yes';
            this.performEncode();
        },

        // 插入解码示例
        insertDecodeExample() {
            this.decodeInput = 'Hello%20%E4%B8%96%E7%95%8C%21%20How%20are%20you%3F%20%26%20fine%3Dyes';
            this.performDecode();
        },

        // 复制结果
        copyResult(type) {
            const textToCopy = type === 'encode' ? this.encodedResult : this.decodedResult;

            if (!textToCopy) return;

            navigator.clipboard.writeText(textToCopy)
                .then(() => {
                    this.showCopyNotification = true;
                    setTimeout(() => {
                        this.showCopyNotification = false;
                    }, 2000);
                })
                .catch(err => {
                    console.error('复制失败:', err);
                    // 降级方案
                    const textarea = document.createElement('textarea');
                    textarea.value = textToCopy;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);

                    this.showCopyNotification = true;
                    setTimeout(() => {
                        this.showCopyNotification = false;
                    }, 2000);
                });
        },

        // 检查字符是否为安全字符
        isSafeChar(char) {
            const safeChars = /[A-Za-z0-9\-_.!~*'()]/;
            return safeChars.test(char);
        },

        // 统计特殊字符数量
        countSpecialChars(str) {
            return [...str].filter(char => !this.isSafeChar(char)).length;
        },

        // 统计百分比编码序列数量
        countPercentSequences(str) {
            const percentSequenceRegex = /%[0-9A-Fa-f]{2}/g;
            const matches = str.match(percentSequenceRegex);
            return matches ? matches.length : 0;
        },

        // 提取百分比编码序列
        extractPercentSequences(str) {
            const percentSequenceRegex = /%[0-9A-Fa-f]{2}/g;
            const matches = str.match(percentSequenceRegex);
            return matches || [];
        },

        // 解码单个百分比序列
        decodePercentSequence(seq) {
            try {
                return decodeURIComponent(seq);
            } catch (error) {
                return '?';
            }
        }
    },
    watch: {
        // 监听输入变化
        encodeInput() {
            this.performEncode();
        },

        decodeInput() {
            this.performDecode();
        }
    },
    mounted() {
        // 加载时插入编码示例
        this.insertEncodeExample();

        // 延迟插入解码示例，避免冲突
        setTimeout(() => {
            this.insertDecodeExample();
        }, 100);
    }
};
</script>

<style scoped>
.uri-encoder {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem 1.5rem;
    min-height: 100vh;
    color: #1e293b;
}

/* 头部样式 */
.header {
    margin-bottom: 2.5rem;
    text-align: center;
}

.title-container {
    background: white;
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
}

.main-title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    font-size: 2.2rem;
    margin: 0 0 0.5rem 0;
    color: #0f172a;
}

.title-icon {
    font-size: 2.5rem;
}

.subtitle {
    font-size: 1.1rem;
    color: #64748b;
    margin: 0;
}

/* 内容包装器 */
.content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

/* 通用卡片样式 */
.section-card {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
}

.section-header {
    padding: 1.5rem 2rem;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0;
    font-size: 1.5rem;
}

.section-icon {
    font-size: 1.8rem;
}

/* 理论区域 */
.theory-content {
    padding: 2rem;
}

.definition {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    border-left: 4px solid #6366f1;
}

.definition-text {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.6;
    color: #334155;
}

.comparison-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-bottom: 2rem;
}

.function-card {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.function-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}

.encode-card .function-header {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.decode-card .function-header {
    background: linear-gradient(135deg, #10b981 0%, #047857 100%);
}

.function-header {
    padding: 1.5rem;
    color: white;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.function-icon {
    font-size: 2rem;
}

.function-title {
    margin: 0;
    font-size: 1.3rem;
}

.function-content {
    padding: 1.5rem;
}

.function-desc {
    margin: 0 0 1.5rem 0;
    line-height: 1.6;
    color: #475569;
}

.use-case {
    margin-bottom: 1.5rem;
}

.use-case-label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #334155;
}

.use-case-list {
    margin: 0;
    padding-left: 1.5rem;
    color: #475569;
}

.use-case-list li {
    margin-bottom: 0.25rem;
}

.example {
    background: #f8fafc;
    border-radius: 8px;
    padding: 1rem;
    border: 1px solid #e2e8f0;
}

.example-label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #334155;
}

.example-code {
    background: #1e293b;
    color: #e2e8f0;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    font-size: 0.9rem;
    display: inline-block;
    margin-bottom: 0.5rem;
}

.example-result {
    color: #475569;
}

/* 重要说明 */
.important-note {
    background: #fff7ed;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #fdba74;
}

.note-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.note-icon {
    font-size: 1.5rem;
}

.note-title {
    margin: 0;
    color: #9a3412;
}

.note-content {
    color: #78350f;
}

.difference-list {
    margin: 1rem 0;
    padding-left: 1.5rem;
}

.difference-list li {
    margin-bottom: 0.5rem;
}

.comparison-example {
    background: white;
    border-radius: 8px;
    padding: 1rem;
    margin-top: 1rem;
}

.comparison-item {
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
}

.comparison-label {
    color: #475569;
    margin-right: 1rem;
}

.comparison-code {
    background: #f1f5f9;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    color: #0f172a;
}

/* 演示区域 */
.demo-container {
    padding: 2rem;
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
}

.demo-group {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e2e8f0;
}

.demo-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.demo-title {
    margin: 0;
    font-size: 1.3rem;
    color: #0f172a;
}

.example-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: #6366f1;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.example-btn:hover {
    background: #4f46e5;
    transform: translateY(-1px);
}

.input-group,
.result-group {
    margin-bottom: 1.5rem;
}

.input-label,
.result-label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #334155;
}

.demo-textarea {
    width: 100%;
    border: 2px solid #cbd5e1;
    border-radius: 8px;
    font-size: 1rem;
    resize: vertical;
    background: white;
    transition: border-color 0.2s ease;
}

.demo-textarea:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.input-meta {
    display: flex;
    justify-content: space-between;
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #64748b;
}

.result-display {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: #1e293b;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 0.5rem;
}

.result-code {
    flex: 1;
    color: #e2e8f0;
    font-size: 0.95rem;
    word-break: break-all;
    line-height: 1.5;
}

.copy-btn {
    background: #475569;
    color: white;
    border: none;
    border-radius: 6px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;
}

.copy-btn:hover {
    background: #6366f1;
}

.result-meta {
    display: flex;
    justify-content: space-between;
    font-size: 0.875rem;
    color: #64748b;
}

.decode-status.error {
    color: #ef4444;
}

.error-message {
    margin-top: 0.75rem;
    padding: 0.75rem;
    background: #fef2f2;
    border-radius: 6px;
    color: #dc2626;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
}

/* 字符分析 */
.char-analysis,
.encoding-breakdown {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e2e8f0;
}

.analysis-title {
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
    color: #334155;
}

.char-grid,
.sequence-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.char-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem;
    border-radius: 6px;
    min-width: 40px;
    font-size: 0.9rem;
    transition: transform 0.2s ease;
}

.char-item:hover {
    transform: scale(1.1);
}

.char-item.safe {
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
}

.char-item.encoded {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

.char-display {
    font-size: 1.2rem;
    margin-bottom: 0.25rem;
}

.char-code {
    font-size: 0.7rem;
    opacity: 0.8;
}

.legend {
    display: flex;
    gap: 1.5rem;
    font-size: 0.875rem;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.legend-color {
    width: 16px;
    height: 16px;
    border-radius: 4px;
}

.legend-item.safe .legend-color {
    background: #d1fae5;
    border: 1px solid #a7f3d0;
}

.legend-item.encoded .legend-color {
    background: #fee2e2;
    border: 1px solid #fecaca;
}

/* 编码序列解析 */
.sequence-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #e2e8f0;
    border-radius: 6px;
    font-size: 0.9rem;
}

.sequence-code {
    color: #475569;
}

.sequence-arrow {
    color: #94a3b8;
}

.sequence-char {
    background: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    color: #0f172a;
    min-width: 24px;
    text-align: center;
}

/* 实际应用示例 */
.real-world-example {
    background: #f0f9ff;
    border-radius: 12px;
    padding: 1.5rem;
    margin-top: 2rem;
    border: 1px solid #bae6fd;
}

.example-title {
    margin: 0 0 1.5rem 0;
    color: #0369a1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.url-example {
    margin-bottom: 1.5rem;
}

.url-original,
.url-encoded,
.url-result {
    margin-bottom: 1rem;
    padding: 0.75rem;
    background: white;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.url-label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #475569;
    font-size: 0.9rem;
}

.url-code {
    font-size: 0.9rem;
    color: #0f172a;
    word-break: break-all;
}

.highlight {
    background: #fef3c7;
    padding: 0.125rem 0.25rem;
    border-radius: 4px;
    color: #92400e;
}

.code-snippet {
    background: #1e293b;
    border-radius: 8px;
    overflow: hidden;
}

.snippet-code {
    margin: 0;
    padding: 1.5rem;
    overflow-x: auto;
}

.snippet-code code {
    color: #e2e8f0;
    font-size: 0.9rem;
    line-height: 1.6;
}

/* 总结区域 */
.summary-content {
    padding: 2rem;
}

.summary-points {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-bottom: 2.5rem;
}

.point-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e2e8f0;
}

.point-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
}

.point-title {
    margin: 0 0 1rem 0;
    font-size: 1.2rem;
    color: #0f172a;
}

.point-list {
    margin: 0;
    padding-left: 1.5rem;
    color: #475569;
}

.point-list li {
    margin-bottom: 0.5rem;
    line-height: 1.5;
}

/* 快速参考表 */
.cheatsheet {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e2e8f0;
}

.cheatsheet-title {
    margin: 0 0 1.5rem 0;
    font-size: 1.2rem;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.cheatsheet-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.95rem;
}

.cheatsheet-table th {
    background: #6366f1;
    color: white;
    text-align: left;
    padding: 0.75rem 1rem;
    font-weight: 600;
}

.cheatsheet-table td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e2e8f0;
}

.cheatsheet-table tr:nth-child(even) {
    background: rgba(99, 102, 241, 0.05);
}

.cheatsheet-table tr:hover {
    background: rgba(99, 102, 241, 0.1);
}

/* 复制通知 */
.copy-notification {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    background: #10b981;
    color: white;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    animation: slideIn 0.3s ease;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .uri-encoder {
        padding: 1rem;
    }

    .main-title {
        font-size: 1.8rem;
        flex-direction: column;
        gap: 0.5rem;
    }

    .section-header {
        padding: 1.25rem 1.5rem;
    }

    .theory-content,
    .demo-container,
    .summary-content {
        padding: 1.5rem;
    }

    .comparison-grid,
    .summary-points {
        grid-template-columns: 1fr;
    }

    .demo-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .example-btn {
        align-self: flex-end;
    }

    .cheatsheet-table {
        font-size: 0.85rem;
    }

    .cheatsheet-table th,
    .cheatsheet-table td {
        padding: 0.5rem 0.75rem;
    }
}
</style>